/**
 * UpdateNotificationBanner Component
 * 
 * Visual indicator showing when an item has been updated by another user.
 * Displays:
 * - Who updated the item (user name + avatar)
 * - When it was updated
 * - Refresh button to reload data
 * - Dismiss button to hide the notification
 */

import React from 'react';
import { RefreshCw, X, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

export interface UpdateNotificationBannerProps {
  /** Whether item has been updated */
  hasPendingUpdate: boolean;
  /** Who updated it */
  updatedByName: string | null;
  /** Avatar URL of who updated */
  updatedByAvatar?: string | null;
  /** When it was updated */
  updatedAt: string | null;
  /** Callback to refresh data */
  onRefresh: () => void;
  /** Callback to dismiss notification */
  onDismiss: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether refresh is in progress */
  isRefreshing?: boolean;
}

export function UpdateNotificationBanner({
  hasPendingUpdate,
  updatedByName,
  updatedByAvatar,
  updatedAt,
  onRefresh,
  onDismiss,
  className,
  size = 'md',
  isRefreshing = false,
}: UpdateNotificationBannerProps) {
  const { t } = usePlatformTranslation();

  // Don't render if no pending update
  if (!hasPendingUpdate) {
    return null;
  }

  // Format the updated time
  const formatUpdatedTime = () => {
    if (!updatedAt) return '';
    
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffMs = now.getTime() - updatedDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return t('common.justNow', 'just now');
    } else if (diffMins < 60) {
      return t('common.minutesAgo', '{{count}} min ago', { count: diffMins });
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return t('common.hoursAgo', '{{count}}h ago', { count: diffHours });
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Size-based styles
  const sizeStyles = {
    sm: {
      banner: 'py-1.5 px-3 gap-2 text-xs',
      avatar: 'w-5 h-5 text-[10px]',
      icon: 'w-3 h-3',
    },
    md: {
      banner: 'py-2 px-4 gap-3 text-sm',
      avatar: 'w-7 h-7 text-xs',
      icon: 'w-4 h-4',
    },
    lg: {
      banner: 'py-3 px-5 gap-4 text-base',
      avatar: 'w-9 h-9 text-sm',
      icon: 'w-5 h-5',
    },
  };

  const styles = sizeStyles[size];

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg',
        'bg-blue-50 dark:bg-blue-950/30',
        'border border-blue-200 dark:border-blue-800',
        styles.banner,
        className
      )}
    >
      {/* Left side - Update info */}
      <div className="flex items-center gap-3">
        {/* Info icon */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50">
          <Info className={cn(styles.icon, 'text-blue-600 dark:text-blue-400')} />
        </div>

        {/* User info */}
        <div className="flex items-center gap-2">
          {/* Avatar */}
          {updatedByAvatar ? (
            <img
              src={updatedByAvatar}
              alt={updatedByName || 'User'}
              className={cn(styles.avatar, 'rounded-full object-cover')}
            />
          ) : (
            <div
              className={cn(
                styles.avatar,
                'rounded-full flex items-center justify-center',
                'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 font-medium'
              )}
            >
              {getInitials(updatedByName)}
            </div>
          )}

          {/* Name and message */}
          <div className="flex flex-col">
            <span className="font-medium text-blue-900 dark:text-blue-100">
              {t('update.dataUpdated', 'Data updated')}
            </span>
            <span className="text-blue-700 dark:text-blue-400 text-xs">
              {t('update.byUser', 'by {{name}}', { name: updatedByName || 'Unknown User' })} · {formatUpdatedTime()}
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        <Badge 
          variant="secondary" 
          className="gap-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 border-0"
        >
          <span>{t('update.newChanges', 'New changes')}</span>
        </Badge>

        {/* Refresh button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-8 px-3 text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 hover:bg-blue-200/50 dark:hover:bg-blue-800/50"
        >
          <RefreshCw className={cn('w-4 h-4 mr-1.5', isRefreshing && 'animate-spin')} />
          <span>{t('update.refresh', 'Refresh')}</span>
        </Button>

        {/* Dismiss button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-7 w-7 p-0 text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 hover:bg-blue-200/50 dark:hover:bg-blue-800/50"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Compact update indicator for inline use
 */
export function UpdateIndicator({
  hasPendingUpdate,
  size = 'sm',
  className,
  onClick,
}: {
  hasPendingUpdate: boolean;
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
}) {
  const { t } = usePlatformTranslation();

  if (!hasPendingUpdate) {
    return null;
  }

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <Badge 
      variant="secondary" 
      className={cn(
        'gap-1 cursor-pointer bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/30',
        className
      )}
      onClick={onClick}
    >
      <Info className={iconSize} />
      <span>{t('update.updated', 'Updated')}</span>
    </Badge>
  );
}

export default UpdateNotificationBanner;
